import React, { useMemo } from "react";
import { Icon, Stack, Text } from "@fluentui/react";

interface RawIndicatorRow {
  sectionName?: string;
  metricName?: string;
  factorLabel?: string;
  value: string | number;
}

interface SHIScoreProps {
  rows: RawIndicatorRow[];
}

// ─── helpers ─────────────────────────────────────────────────────────────────

const toNum = (v: string | number | undefined): number => {
  if (v === undefined) return 0;
  const n = Number(String(v).replace(/,/g, "").trim());
  return Number.isFinite(n) ? n : 0;
};

const fmtNum = (n: number): string => n.toLocaleString();

// Health-colour for a score (0-100 scale)
const scoreColor = (score: number): string => {
  if (score >= 71) return "#107C10";
  if (score >= 41) return "#FFB900";
  return "#D13438";
};

// ─── data parsing ─────────────────────────────────────────────────────────────

interface CaseRiskData {
  totalOpen: number;
  riskyTotal: number;
  veryHigh: number;
  high: number;
  medium: number;
  lowRisk: number;
  // score derivation (if provided as rows — otherwise computed)
  avgRiskScore?: number;
  globalMax?: number;
  productScore?: number;
}

function parseSHIData(rows: RawIndicatorRow[]): CaseRiskData {
  let totalOpen = 0;
  let riskyTotal = 0;
  let veryHigh = 0;
  let high = 0;
  let medium = 0;
  let lowRisk = 0;
  let avgRiskScore: number | undefined;
  let globalMax: number | undefined;
  let productScore: number | undefined;

  for (const row of rows) {
    const metric = (row.metricName ?? "").trim();
    const factor = (row.factorLabel ?? "").trim();
    const val = toNum(row.value);

    if (metric === "Total Open" && factor === "") {
      totalOpen = val;
    } else if (metric === "Risky Cases (used in score)") {
      if (factor === "") {
        riskyTotal = val;
      } else if (factor === "Very High") {
        veryHigh = val;
      } else if (factor === "High") {
        high = val;
      } else if (factor === "Medium") {
        medium = val;
      }
    } else if (metric === "Low Risk (not in score)" && factor === "") {
      lowRisk = val;
    } else if (metric === "Avg Risk Score") {
      avgRiskScore = val;
    } else if (metric === "GlobalMax") {
      globalMax = val;
    } else if (metric === "ProductScore") {
      productScore = val;
    }
  }

  return { totalOpen, riskyTotal, veryHigh, high, medium, lowRisk, avgRiskScore, globalMax, productScore };
}

// ─── sub-components ──────────────────────────────────────────────────────────

interface CountBoxProps {
  label: string;
  value: number;
  accent?: string;
  bg?: string;
  border?: string;
  note?: string;
  bold?: boolean;
}

const CountBox: React.FC<CountBoxProps> = ({ label, value, accent = "#323130", bg = "#FFFFFF", border = "#EDEBE9", note, bold }) => (
  <Stack
    styles={{
      root: {
        backgroundColor: bg,
        border: `1px solid ${border}`,
        borderRadius: 8,
        padding: "12px 16px",
        minWidth: 110,
        alignItems: "center",
        justifyContent: "center",
        gap: 2,
      },
    }}
    horizontalAlign="center"
    verticalAlign="center"
    tokens={{ childrenGap: 2 }}
  >
    <Text variant="small" styles={{ root: { color: accent, fontWeight: 600, textAlign: "center", lineHeight: "16px" } }}>
      {label}
    </Text>
    <Text variant="xxLarge" styles={{ root: { fontWeight: 700, color: accent, lineHeight: "36px" } }}>
      {fmtNum(value)}
    </Text>
    {note && (
      <Text variant="xSmall" styles={{ root: { color: "#A19F9D", textAlign: "center" } }}>
        {note}
      </Text>
    )}
    {bold && (
      <Text variant="xSmall" styles={{ root: { color: accent, fontWeight: 600 } }}>
        ▲ used in score
      </Text>
    )}
  </Stack>
);

interface SeverityBoxProps {
  label: string;
  value: number;
  accent: string;
  bg: string;
  border: string;
}

const SeverityBox: React.FC<SeverityBoxProps> = ({ label, value, accent, bg, border }) => (
  <Stack
    horizontalAlign="center"
    verticalAlign="center"
    tokens={{ childrenGap: 2 }}
    styles={{
      root: {
        backgroundColor: bg,
        border: `1.5px solid ${border}`,
        borderRadius: 8,
        padding: "10px 20px",
        minWidth: 100,
        flex: "1 1 0",
      },
    }}
  >
    <Text variant="medium" styles={{ root: { color: accent, fontWeight: 700, textAlign: "center" } }}>
      {label}
    </Text>
    <Text variant="xxLarge" styles={{ root: { fontWeight: 700, color: accent, lineHeight: "36px" } }}>
      {fmtNum(value)}
    </Text>
  </Stack>
);

interface DerivationRowProps {
  label: string;
  formula: string;
  bold?: boolean;
}

const DerivationRow: React.FC<DerivationRowProps> = ({ label, formula, bold }) => (
  <Stack
    horizontal
    horizontalAlign="space-between"
    verticalAlign="center"
    styles={{ root: { padding: "6px 0", borderBottom: "1px solid #EDEBE9" } }}
  >
    <Text variant="medium" styles={{ root: { fontWeight: bold ? 700 : 400, color: bold ? "#323130" : "#605E5C", minWidth: 160 } }}>
      {label}
    </Text>
    <Text variant="medium" styles={{ root: { fontWeight: bold ? 700 : 600, color: "#323130", fontFamily: "Consolas, monospace", textAlign: "right" } }}>
      {formula}
    </Text>
  </Stack>
);

// ─── main export ──────────────────────────────────────────────────────────────

const IntuneSHIScoreRawIndicatorCards: React.FC<SHIScoreProps> = ({ rows }) => {
  const data = useMemo(() => parseSHIData(rows), [rows]);

  // Compute score derivation when not provided in data
  const avgRisk = data.avgRiskScore ?? (data.riskyTotal > 0 ? data.riskyTotal : undefined);
  const normHealth = avgRisk !== undefined ? +(avgRisk / 100).toFixed(2) : undefined;
  const severity = normHealth !== undefined ? +(1 - normHealth).toFixed(2) : undefined;
  const volumeFactor = data.riskyTotal > 0
    ? +(Math.log(data.riskyTotal) / Math.log(Math.max(data.riskyTotal, 1))).toFixed(1)
    : undefined;
  const compositeRisk = (severity !== undefined && volumeFactor !== undefined)
    ? +(severity * volumeFactor).toFixed(2)
    : undefined;
  const globalMax = data.globalMax ?? 3.5;
  const productScore = data.productScore
    ?? (compositeRisk !== undefined ? +((1 - compositeRisk / globalMax) * 100).toFixed(2) : undefined);

  const hasDerivation = avgRisk !== undefined && productScore !== undefined;

  return (
    <Stack horizontal wrap tokens={{ childrenGap: 16 }} styles={{ root: { alignItems: "flex-start" } }}>

      {/* ── LEFT: Case Risk Profile ── */}
      <Stack
        tokens={{ childrenGap: 16 }}
        styles={{
          root: {
            backgroundColor: "#FFFBF0",
            border: "1.5px solid #FFB900",
            borderRadius: 12,
            padding: "20px 24px",
            minWidth: 380,
            maxWidth: 520,
            flex: "1 1 380px",
          },
        }}
      >
        {/* header */}
        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
          <Icon iconName="Warning" styles={{ root: { fontSize: 18, color: "#FFB900" } }} />
          <Text variant="large" styles={{ root: { fontWeight: 700, color: "#C15A00" } }}>
            Case Risk Profile
          </Text>
        </Stack>

        {/* example note */}
        <Text variant="small" styles={{ root: { color: "#605E5C", marginTop: -8 } }}>
          Proactive support case analysis for this tenant.
        </Text>

        {/* top row: Total Open · Risky Cases · Low Risk */}
        <Stack horizontal tokens={{ childrenGap: 10 }} styles={{ root: { flexWrap: "wrap" } }}>
          <CountBox
            label="Total Open"
            value={data.totalOpen}
            bg="#FFFFFF"
            border="#EDEBE9"
          />
          <CountBox
            label="⚠ Risky Cases (used in score)"
            value={data.riskyTotal}
            accent="#D13438"
            bg="#FDF3F3"
            border="#D13438"
          />
          <CountBox
            label="Low Risk"
            value={data.lowRisk}
            accent="#107C10"
            bg="#F1FBF1"
            border="#107C10"
            note="not in score"
          />
        </Stack>

        {/* breakdown triangle header */}
        <Stack horizontalAlign="center" tokens={{ childrenGap: 8 }}>
          <Text variant="medium" styles={{ root: { fontWeight: 600, color: "#C15A00" } }}>
            ▼ breakdown of {fmtNum(data.riskyTotal)} Risky Cases
          </Text>

          <Stack horizontal tokens={{ childrenGap: 10 }} styles={{ root: { width: "100%" } }}>
            <SeverityBox
              label="Very High"
              value={data.veryHigh}
              accent="#D13438"
              bg="#FDF3F3"
              border="#D13438"
            />
            <SeverityBox
              label="High"
              value={data.high}
              accent="#C15A00"
              bg="#FFF7F0"
              border="#C15A00"
            />
            <SeverityBox
              label="Medium"
              value={data.medium}
              accent="#FFB900"
              bg="#FFFBF0"
              border="#FFB900"
            />
          </Stack>

          {/* sum equation */}
          <Text variant="smallPlus" styles={{ root: { color: "#605E5C" } }}>
            {fmtNum(data.veryHigh)} + {fmtNum(data.high)} + {fmtNum(data.medium)}
            {" = "}
            <Text variant="smallPlus" styles={{ root: { fontWeight: 700, color: "#D13438" } }}>
              {fmtNum(data.riskyTotal)} Risky Cases
            </Text>
          </Text>
        </Stack>

        {/* divider */}
        <div style={{ height: 1, backgroundColor: "#EDEBE9" }} />

        {/* hierarchy note */}
        <Stack tokens={{ childrenGap: 4 }}>
          <Text variant="small" styles={{ root: { color: "#323130" } }}>
            <strong>Hierarchy:</strong> Total Open Cases = Risky Cases + Low Risk (non-risky).
          </Text>
          <Text variant="small" styles={{ root: { color: "#323130" } }}>
            Risky Cases breakdown: Very High + High + Medium.
          </Text>
          <Text variant="small" styles={{ root: { color: "#D13438" } }}>
            ⚠ <strong>Low Risk cases are NOT part of the SHI Proactive Score calculation.</strong>{" "}
            Only Risky cases (Very High, High, Medium) contribute to Proactive Support Health Score.
          </Text>
        </Stack>
      </Stack>

      {/* ── RIGHT: Score Derivation ── hidden for all products */}
      {false && hasDerivation && (
        <Stack
          tokens={{ childrenGap: 12 }}
          styles={{
            root: {
              backgroundColor: "#EFF6FC",
              border: "1.5px solid #C7E0F4",
              borderRadius: 12,
              padding: "20px 24px",
              minWidth: 360,
              maxWidth: 480,
              flex: "1 1 360px",
            },
          }}
        >
          {/* header */}
          <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
            <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
              <Icon iconName="BarChartVerticalFill" styles={{ root: { fontSize: 18, color: "#0078D4" } }} />
              <Text variant="large" styles={{ root: { fontWeight: 700, color: "#323130" } }}>
                Score Derivation
              </Text>
            </Stack>
            <Stack
              horizontalAlign="center"
              verticalAlign="center"
              styles={{
                root: {
                  backgroundColor: "#0078D4",
                  borderRadius: 20,
                  padding: "4px 14px",
                },
              }}
            >
              <Text variant="medium" styles={{ root: { fontWeight: 700, color: "#FFFFFF" } }}>
                Score: {productScore?.toFixed(2)}
              </Text>
            </Stack>
          </Stack>

          {/* derivation rows */}
          <Stack tokens={{ childrenGap: 0 }}>
            <DerivationRow
              label="Avg Risk Score"
              formula={String(avgRisk)}
            />
            {normHealth !== undefined && (
              <DerivationRow
                label="NormHealth"
                formula={`${avgRisk} / 100 = ${normHealth}`}
              />
            )}
            {severity !== undefined && (
              <DerivationRow
                label="Severity"
                formula={`1 - ${normHealth} = ${severity}`}
              />
            )}
            {volumeFactor !== undefined && (
              <DerivationRow
                label="VolumeFactor"
                formula={`log(${data.riskyTotal})/log(${data.riskyTotal}) = ${volumeFactor}`}
              />
            )}
            {compositeRisk !== undefined && (
              <DerivationRow
                label="CompositeRisk"
                formula={`${severity} x ${volumeFactor} = ${compositeRisk}`}
              />
            )}
            <DerivationRow
              label="GlobalMax (month)"
              formula={String(globalMax)}
            />
            {productScore !== undefined && (
              <DerivationRow
                label="ProductScore"
                formula={`(1 - ${compositeRisk}/${globalMax}) x 100 = ${productScore?.toFixed(2)}`}
                bold
              />
            )}
          </Stack>

          {/* scoring logic footer */}
          <Stack
            styles={{
              root: {
                backgroundColor: "#DDEEFF",
                borderRadius: 6,
                padding: "10px 12px",
              },
            }}
          >
            <Text variant="small" styles={{ root: { color: "#0F4B8C", lineHeight: "18px" } }}>
              <strong>Scoring Logic:</strong> CompositeRisk = 0 → 100 (perfect) | = GlobalMax → 0 (worst) | Between → proportional
            </Text>
          </Stack>
        </Stack>
      )}

      {/* if no derivation data, show a note — hidden for all products */}
      {false && !hasDerivation && (
        <Stack
          tokens={{ childrenGap: 12 }}
          styles={{
            root: {
              backgroundColor: "#EFF6FC",
              border: "1.5px solid #C7E0F4",
              borderRadius: 12,
              padding: "20px 24px",
              minWidth: 320,
              maxWidth: 400,
              flex: "1 1 320px",
            },
          }}
        >
          <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
            <Icon iconName="BarChartVerticalFill" styles={{ root: { fontSize: 18, color: "#0078D4" } }} />
            <Text variant="large" styles={{ root: { fontWeight: 700, color: "#323130" } }}>
              Score Derivation
            </Text>
          </Stack>

          {/* derived from available counts */}
          <Stack tokens={{ childrenGap: 0 }}>
            <DerivationRow label="Total Open" formula={fmtNum(data.totalOpen)} />
            <DerivationRow label="Risky Cases" formula={fmtNum(data.riskyTotal)} />
            <DerivationRow label="Low Risk (not in score)" formula={fmtNum(data.lowRisk)} />
            <DerivationRow label="Very High" formula={fmtNum(data.veryHigh)} />
            <DerivationRow label="High" formula={fmtNum(data.high)} />
            <DerivationRow label="Medium" formula={fmtNum(data.medium)} />
          </Stack>

          <Stack
            styles={{
              root: {
                backgroundColor: "#DDEEFF",
                borderRadius: 6,
                padding: "10px 12px",
              },
            }}
          >
            <Text variant="small" styles={{ root: { color: "#0F4B8C", lineHeight: "18px" } }}>
              <strong>Scoring Logic:</strong> CompositeRisk = 0 → 100 (perfect) | = GlobalMax → 0 (worst) | Between → proportional
            </Text>
          </Stack>
        </Stack>
      )}
    </Stack>
  );
};

export default IntuneSHIScoreRawIndicatorCards;
